import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Flex, Form, InputNumber } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { routes } from "router"
import { Card } from "ui/Card"
import { CustomForm } from "ui/Form"
import { Text, Title } from "ui/Typography"
import { spacings } from "themes/variables"
import { savePowerTranzCardFee } from "store/payment-provider"
import { getPowerTranzErrorCode, getPowerTranzErrorMessage } from "helpers/powertranz"
import { POWERTRANZ_CARD_FEE_MAX_PERCENT } from "constants/powertranz"
import { grossUpCardFee } from "utils/bookingCalculations"
import { numberToCurrency } from "helpers/string"
import { useCardProcessingFee } from "pagesV2/pro/Settings/TaxesAndFees/hooks/useCardProcessingFee"

// The processing fee the guide passes on with PowerTranz charges. PowerTranz publishes no rate, so
// the guide enters their own percent; like Stripe's, it is added to guests' card payments only while
// "pass card fee to guest" is on in Taxes & Fees, which this form reads but doesn't change.
// The booking amount the fee example is worked out on.
const EXAMPLE_AMOUNT = 100

const CardFeeForm = ({ cardFeeRate }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const isSaving = useSelector((state) => state.paymentProvider.savePowerTranzCardFee.loading)
  const { passFeeToGuest, isLoading: isPassthroughLoading } = useCardProcessingFee({ enabled: true })
  // "saved" or an error message, until the next save.
  const [result, setResult] = useState(null)
  const typedRate = Form.useWatch("card_fee_rate", form)
  // The fee is grossed up like the checkout's (rate / (1 - rate)) so the guide nets the full price,
  // which is why 3.5% on $100 is $3.63, not $3.50. The example says so before a guest sees it.
  const exampleFee = typedRate > 0 && typedRate <= POWERTRANZ_CARD_FEE_MAX_PERCENT ? grossUpCardFee(EXAMPLE_AMOUNT, { rate: typedRate / 100, fixed: 0 }) : 0

  useEffect(() => {
    form.setFieldsValue({ card_fee_rate: cardFeeRate || null })
  }, [form, cardFeeRate])

  const handleSave = async ({ card_fee_rate }) => {
    setResult(null)
    try {
      await dispatch(savePowerTranzCardFee({ card_fee_rate: card_fee_rate ?? 0 }))
      setResult({ type: "success", message: t("powertranz.settings.card_fee.saved") })
    } catch (error) {
      const fieldMessage = [].concat(error?.data?.message?.card_fee_rate || [])[0]
      if (fieldMessage) form.setFields([{ name: "card_fee_rate", errors: [fieldMessage] }])
      setResult({ type: "error", message: fieldMessage || getPowerTranzErrorMessage(t, getPowerTranzErrorCode(error)) })
    }
  }

  return (
    <Card>
      <Flex vertical gap={spacings.M}>
        <Flex vertical gap={spacings.XS}>
          <Title level={4}>{t("powertranz.settings.card_fee.title")}</Title>
          <Text>{t("powertranz.settings.card_fee.description")}</Text>
        </Flex>
        <CustomForm form={form} layout="vertical" onFinish={handleSave} disabled={isSaving} autoComplete="off">
          <Form.Item
            label={t("powertranz.settings.card_fee.label")}
            name="card_fee_rate"
            extra={t("powertranz.settings.card_fee.hint", { max: POWERTRANZ_CARD_FEE_MAX_PERCENT })}
            rules={[
              {
                type: "number",
                min: 0,
                max: POWERTRANZ_CARD_FEE_MAX_PERCENT,
                message: t("powertranz.settings.card_fee.range", { max: POWERTRANZ_CARD_FEE_MAX_PERCENT })
              }
            ]}
          >
            <InputNumber size="large" min={0} max={POWERTRANZ_CARD_FEE_MAX_PERCENT} step={0.1} precision={2} addonAfter="%" placeholder="0" />
          </Form.Item>
          <Flex vertical gap={spacings.S}>
            {exampleFee > 0 && (
              <Text variant="caption">
                {t("powertranz.settings.card_fee.example", {
                  amount: numberToCurrency(EXAMPLE_AMOUNT),
                  charged: numberToCurrency(EXAMPLE_AMOUNT + exampleFee),
                  fee: numberToCurrency(exampleFee)
                })}
              </Text>
            )}
            {!isPassthroughLoading && (
              <Text variant="caption">
                {passFeeToGuest ? t("powertranz.settings.card_fee.passthrough_on") : t("powertranz.settings.card_fee.passthrough_off")}{" "}
                <Link to={`${routes.proSettingsTaxesAndFees()}?tab=card_processing_fee`}>{t("powertranz.settings.card_fee.passthrough_link")}</Link>
              </Text>
            )}
            {result && <Alert showIcon type={result.type} message={result.message} />}
            <Flex>
              <Button className="pr-l pl-l" color="primary" variant="solid" size="large" htmlType="submit" loading={isSaving}>
                {t("powertranz.settings.card_fee.save")}
              </Button>
            </Flex>
          </Flex>
        </CustomForm>
      </Flex>
    </Card>
  )
}

CardFeeForm.propTypes = {
  // The saved rate in percent (0 = none).
  cardFeeRate: PropTypes.number
}

CardFeeForm.defaultProps = {
  cardFeeRate: 0
}

export default CardFeeForm
